import { useCallback, useContext } from "react";
import { isEmpty, makeApiCall } from "@swiggy-private/js-utils";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import {
    TableContext,
    COMMON_LOGIN_STATUS_ERROR_CODE,
    COMMON_PIN_NOT_GENERATED_STATUS_ERROR_CODE,
    loginHelper,
    ToastType,
    QrCodeActions,
    ResponseContext,
} from "@swiggy-private/portal-dineout-context";
import { useActions } from "./useActions";
import { API_ENDPOINTS, DINEOUT_CART_API_HOST, SUCCESS_STATUS_CODE } from "../constants/api";
import { ERROR_MESSAGES } from "../constants/messages";
import { customApiCall, IOptions } from "../utils/api";
import { getBridgeSdk } from "../utils/bridgeSdk";

export interface IBillPaymentResponse {
    title: string;
    description: string;
    imageId: string;
    ctas: Array<{
        text: string;
        link: string;
        dlsType: string;
        type: NavigationTypes;
    }>;
}

export const useBillPayment = () => {
    const { handleLogin, handlePin } = useActions();
    const { dispatch } = useContext(ResponseContext);
    const { restaurantId = "", tableId = "", parentOrderId = "" } = useContext(TableContext);

    const billPayment = useCallback(async (): Promise<IBillPaymentResponse | false> => {
        if (!handleLogin() || !handlePin()) {
            return false;
        }

        const bodyParams: {
            restaurantId: string;
            tableId: string;
            parentOrderId?: string;
        } = {
            restaurantId,
            tableId,
        };

        // Include parentOrderId only if it exists
        if (parentOrderId) {
            bodyParams.parentOrderId = parentOrderId;
        }

        try {
            const options: IOptions = {
                bodyParams,
                method: "POST",
            };

            const response = await customApiCall<IBillPaymentResponse>({
                _makeApiCall: makeApiCall,
                apiHost: DINEOUT_CART_API_HOST,
                apiEndpoint: API_ENDPOINTS.GET_PAY_BILL_CONFIGS,
                opts: options,
            });

            if (COMMON_LOGIN_STATUS_ERROR_CODE.includes(response?.statusCode)) {
                loginHelper({
                    sdk: getBridgeSdk(),
                    forwardPath: window.location.pathname + window.location.search,
                });
                return false;
            }

            if (COMMON_PIN_NOT_GENERATED_STATUS_ERROR_CODE.includes(response?.statusCode)) {
                dispatch({
                    type: QrCodeActions.SHOW_TOAST,
                    payload: {
                        type: ToastType.ERROR,
                        message: ERROR_MESSAGES.PIN_NOT_GENERATED,
                    },
                });
                return false;
            }

            if (response?.statusCode === SUCCESS_STATUS_CODE && !isEmpty(response?.data)) {
                return response.data;
            }

            // Handle error case
            dispatch({
                type: QrCodeActions.SHOW_TOAST,
                payload: {
                    type: ToastType.ERROR,
                    message: ERROR_MESSAGES.BILL_PAYMENT_ERROR,
                },
            });
            return false;
        } catch (error) {
            dispatch({
                type: QrCodeActions.SHOW_TOAST,
                payload: {
                    type: ToastType.ERROR,
                    message: ERROR_MESSAGES.BILL_PAYMENT_ERROR,
                },
            });
            return false;
        }
    }, [handleLogin, handlePin, restaurantId, tableId, parentOrderId, dispatch]);

    return { billPayment };
};
